import { useEffect, useRef, useState } from 'react';
import { Box } from '@rsos/components/capstone/base';
import { AlertStatusName } from '@rsos/constants/alertStatuses';
import { useCurrentConversation } from '@rsos/reduxDataStore/src/features/alerts/hooks';
import { useSelectedAlert } from '@rsos/reduxDataStore/src/features/selectors/alertsSelectors';
import TalkJSChatbox from '../TalkJSChatbox';
import AcceptDeclineAlert from './AcceptDeclineAlert';
import {
  AlertChatBoxContainer,
  AlertActions,
  TalkJSContainer,
  CHAT_HEADER_HEIGHT,
} from './AlertChatBox.styles';
import RemoveAlert from './RemoveAlert';
import { shouldShowAlertActions, shouldShowRemoveAlert } from './helpers';

interface AlertChatBoxProps {
  chatBoxHeight: string;
  onRemoveAlertFromQueue: () => void;
  onShowDeclinePane: (show: boolean) => void;
  isChatBoxVisible?: boolean;
  hasPrivilegedChat?: boolean;
}

const AlertChatBox = ({
  chatBoxHeight,
  isChatBoxVisible,
  onRemoveAlertFromQueue,
  onShowDeclinePane,
  hasPrivilegedChat,
}: AlertChatBoxProps) => {
  const currentAlert = useSelectedAlert();

  const alertConversation = useCurrentConversation();

  const selectedAlertID = currentAlert?.alert_id;

  const alertStatus = currentAlert?.status?.name as AlertStatusName | undefined;

  const isSupplementalOnly = currentAlert?.supplemental_only as
    | boolean
    | undefined;

  const alertConversationID = alertConversation?.conversation?.id;

  const alertActionsRef = useRef<HTMLDivElement>(null);
  const removeAlertRef = useRef<HTMLDivElement>(null);

  const [showAlertActions, setShowAlertActions] = useState(
    shouldShowAlertActions(alertStatus),
  );

  const defaultAlertActionsHeight = hasPrivilegedChat
    ? `${CHAT_HEADER_HEIGHT}px`
    : '0px';
  const additionalHeight = hasPrivilegedChat ? CHAT_HEADER_HEIGHT : 0;

  const [alertActionsHeight, setAlertActionsHeight] = useState(
    defaultAlertActionsHeight,
  );

  const [showRemoveAlert, setShowRemoveAlert] = useState(
    shouldShowRemoveAlert(alertStatus, isSupplementalOnly),
  );

  // Updates the visibility of alert actions such as accept, decline, and remove alert.
  useEffect(() => {
    if (selectedAlertID) {
      setShowAlertActions(shouldShowAlertActions(alertStatus));

      setShowRemoveAlert(
        shouldShowRemoveAlert(alertStatus, isSupplementalOnly),
      );
    }
  }, [alertStatus, isChatBoxVisible, isSupplementalOnly, selectedAlertID]);

  // Updates alertActions height to ensure both TalkJS and alert actions are visible on the page.
  useEffect(() => {
    if (selectedAlertID) {
      if (showAlertActions) {
        const currentRef = showRemoveAlert ? removeAlertRef : alertActionsRef;
        setAlertActionsHeight(
          `${(currentRef.current?.clientHeight ?? 0) + additionalHeight}px`,
        );
      } else {
        setAlertActionsHeight(defaultAlertActionsHeight);
      }
    }
  }, [
    additionalHeight,
    selectedAlertID,
    showAlertActions,
    showRemoveAlert,
    defaultAlertActionsHeight,
  ]);

  // Hide decline pane and show remove alert when alert status is canceled, declined, or timed out.
  useEffect(() => {
    if (showRemoveAlert) {
      onShowDeclinePane(false);
    }
  }, [onShowDeclinePane, showRemoveAlert]);

  return (
    <AlertChatBoxContainer isChatBoxVisible={isChatBoxVisible ?? false}>
      {selectedAlertID && alertConversationID && (
        <TalkJSContainer
          chatBoxHeight={chatBoxHeight}
          alertActionsHeight={alertActionsHeight}
          isChatBoxVisible={isChatBoxVisible ?? false}
        >
          <TalkJSChatbox />
        </TalkJSContainer>
      )}
      {showAlertActions && (
        <AlertActions
          alertStatus={alertStatus}
          showRemoveAlert={showRemoveAlert}
          ref={showRemoveAlert ? removeAlertRef : alertActionsRef}
        >
          <Box padding="10px">
            {showRemoveAlert ? (
              <RemoveAlert onRemoveAlertFromQueue={onRemoveAlertFromQueue} />
            ) : (
              <AcceptDeclineAlert onShowDeclinePane={onShowDeclinePane} />
            )}
          </Box>
        </AlertActions>
      )}
    </AlertChatBoxContainer>
  );
};

export default AlertChatBox;
